import React from 'react';
import styled from 'styled-components';
import Card from 'components/Card';
import { usePerformance } from '../context';
import history from '../../../history';

// ─── Styled ───

const OverviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.h3`
  font-family: ${(props) => props.theme.fonts.oswald};
  font-size: 18px;
  color: #2b3d54;
  text-transform: uppercase;
  margin: 0;
`;

const KpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
`;

const KpiCard = styled.div<{ $accent?: boolean }>`
  background: ${(props) => (props.$accent ? '#e25d34' : 'white')};
  color: ${(props) => (props.$accent ? 'white' : '#2b3d54')};
  border-radius: 4px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: ${(props) =>
    props.$accent ? 'none' : '0 1px 3px rgba(0,0,0,0.08)'};
  border: ${(props) => (props.$accent ? 'none' : '1px solid #e8e9eb')};

  .kpi-label {
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    opacity: 0.8;
  }

  .kpi-value {
    font-family: ${(props) => props.theme.fonts.oswald};
    font-size: 32px;
    font-weight: 600;
    line-height: 1;
  }

  .kpi-suffix {
    font-size: 18px;
    font-weight: 400;
    opacity: 0.7;
  }
`;

const TrendArrow = styled.span<{ $dir: 'up' | 'down' | 'flat' }>`
  font-size: 13px;
  font-weight: 600;
  margin-left: 6px;
  ${(props) => {
    switch (props.$dir) {
      case 'up':
        return 'color: #28a745;';
      case 'down':
        return 'color: #dc3545;';
      default:
        return 'color: #6c757d;';
    }
  }}
`;

const ClickableKpiCard = styled(KpiCard)`
  cursor: pointer;
  transition:
    box-shadow 0.15s,
    border-color 0.15s;
  &:hover {
    border-color: #e25d34;
    box-shadow: 0 2px 8px rgba(226, 93, 52, 0.15);
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;

  table {
    width: 100%;
    border-collapse: collapse;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    font-size: 13px;

    th {
      font-family: ${(props) => props.theme.fonts.oswald};
      text-transform: uppercase;
      font-size: 12px;
      font-weight: 500;
      color: #6c757d;
      text-align: left;
      padding: 10px 12px;
      border-bottom: 2px solid #e8e9eb;
      white-space: nowrap;
    }

    td {
      padding: 10px 12px;
      border-bottom: 1px solid #f0f0f0;
      color: #2b3d54;
    }

    tr:hover td {
      background: #fafafa;
    }

    tr.active-market td {
      background: #fef6f3;
      border-bottom-color: #f0d0c4;
    }

    tr.list-stats td {
      background: #e8f4fd;
      border-bottom-color: #b8d9f0;
      font-weight: 600;
    }

    .num {
      text-align: right;
      font-variant-numeric: tabular-nums;
    }
  }
`;

// ─── Component ───

const Overview = () => {
  const { state, getActiveList } = usePerformance();
  const { kpis, marketRows, filters, restaurants, crmData } = state;
  const activeList = getActiveList();

  const currentMarket = filters.market?.label || '';
  const isPanel = filters.viewMode === 'panel';

  // Compute list stats when a list filter is active
  const listStats = activeList
    ? (() => {
        const idSet = new Set(activeList.store_ids);
        const listRestaurants = restaurants.filter((r) =>
          idSet.has(r.store_id)
        );
        const listCrm = crmData.filter((c) => idSet.has(c.restaurant_id));
        const won = listCrm.filter((c) => c.crm_status === 'won').length;
        const lost = listCrm.filter((c) => c.crm_status === 'lost').length;
        const toWinOver = listCrm.filter(
          (c) => c.crm_status === 'to_win_over'
        ).length;
        const withRefs = listRestaurants.filter(
          (r) =>
            r.client_refs_by_wine &&
            Object.keys(r.client_refs_by_wine).length > 0
        ).length;
        const totalRivals = listRestaurants.reduce(
          (sum, r) =>
            sum +
            (r.rival_wines
              ? Object.values(r.rival_wines).reduce((a, b) => a + b, 0)
              : 0),
          0
        );
        return {
          label: activeList.name,
          count: listRestaurants.length,
          dn: withRefs,
          won,
          lost,
          winBack: 0,
          consumptionRate:
            listRestaurants.length > 0
              ? Math.round((withRefs / listRestaurants.length) * 100)
              : 0,
          recurring: 0,
          competitorDn: totalRivals,
          marketShare:
            listRestaurants.length > 0
              ? Math.round((withRefs / listRestaurants.length) * 100)
              : 0,
          toWinOver
        };
      })()
    : null;

  const trend = (current: number, prev: number): 'up' | 'down' | 'flat' => {
    if (current > prev) return 'up';
    if (current < prev) return 'down';
    return 'flat';
  };

  const trendIcon = (dir: 'up' | 'down' | 'flat') => {
    switch (dir) {
      case 'up':
        return '↑';
      case 'down':
        return '↓';
      default:
        return '→';
    }
  };

  const navigateToRestaurants = (status?: string) => {
    const params = status ? `?status=${status}` : '';
    history.push(`/performance/restaurants${params}`);
  };

  return (
    <OverviewContainer>
      {/* ROI Section */}
      <Section>
        <SectionTitle>ROI</SectionTitle>
        <KpiRow>
          <KpiCard>
            <span className="kpi-label">Wine Services Subscription Cost</span>
            <span className="kpi-value">
              {kpis.roi.ws_subscription_cost.toLocaleString()}
              <span className="kpi-suffix"> €</span>
            </span>
          </KpiCard>
          <KpiCard>
            <span className="kpi-label">Upsell</span>
            <span className="kpi-value">
              {kpis.roi.upsell.toLocaleString()}
              <span className="kpi-suffix"> €</span>
            </span>
          </KpiCard>
          <KpiCard $accent>
            <span className="kpi-label">ROI</span>
            <span className="kpi-value">
              {kpis.roi.roi_percent}
              <span className="kpi-suffix"> %</span>
            </span>
          </KpiCard>
        </KpiRow>
      </Section>

      {/* My Performance */}
      <Section>
        <SectionTitle>My Performance</SectionTitle>
        <KpiRow>
          <KpiCard>
            <span className="kpi-label">My Numerical Distribution</span>
            <span className="kpi-value">
              {kpis.performance.numerical_distribution}
              <TrendArrow
                $dir={trend(
                  kpis.performance.numerical_distribution,
                  kpis.performance.numerical_distribution_prev
                )}
              >
                {trendIcon(
                  trend(
                    kpis.performance.numerical_distribution,
                    kpis.performance.numerical_distribution_prev
                  )
                )}
              </TrendArrow>
            </span>
          </KpiCard>
          <ClickableKpiCard onClick={() => navigateToRestaurants('won')}>
            <span className="kpi-label">Won</span>
            <span className="kpi-value" style={{ color: '#28a745' }}>
              {kpis.performance.won}
            </span>
          </ClickableKpiCard>
          <ClickableKpiCard onClick={() => navigateToRestaurants('lost')}>
            <span className="kpi-label">Lost</span>
            <span className="kpi-value" style={{ color: '#dc3545' }}>
              {kpis.performance.lost}
            </span>
          </ClickableKpiCard>
          <KpiCard>
            <span className="kpi-label">Win Back</span>
            <span className="kpi-value" style={{ color: '#fd7e14' }}>
              {kpis.performance.win_back}
            </span>
          </KpiCard>
        </KpiRow>
        <KpiRow style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
          <KpiCard>
            <span className="kpi-label">Consumption Rate</span>
            <span className="kpi-value">
              {kpis.performance.consumption_rate}
              <span className="kpi-suffix"> %</span>
            </span>
          </KpiCard>
          <KpiCard>
            <span className="kpi-label">Recurring</span>
            <span className="kpi-value">
              {kpis.performance.recurring_percent}
              <span className="kpi-suffix"> %</span>
            </span>
          </KpiCard>
        </KpiRow>
      </Section>

      {/* My Opportunities */}
      <Section>
        <SectionTitle>My Opportunities</SectionTitle>
        <KpiRow>
          <KpiCard>
            <span className="kpi-label">
              {isPanel ? 'Panel DN' : 'Competitor DN'}
            </span>
            <span className="kpi-value">
              {isPanel
                ? kpis.opportunities.panel_dn
                : kpis.opportunities.competitor_dn}
              <TrendArrow
                $dir={trend(
                  isPanel
                    ? kpis.opportunities.panel_dn
                    : kpis.opportunities.competitor_dn,
                  isPanel
                    ? kpis.opportunities.panel_dn_prev
                    : kpis.opportunities.competitor_dn_prev
                )}
              >
                {trendIcon(
                  trend(
                    isPanel
                      ? kpis.opportunities.panel_dn
                      : kpis.opportunities.competitor_dn,
                    isPanel
                      ? kpis.opportunities.panel_dn_prev
                      : kpis.opportunities.competitor_dn_prev
                  )
                )}
              </TrendArrow>
            </span>
          </KpiCard>
          <KpiCard>
            <span className="kpi-label">
              {isPanel ? 'My Market Penetration' : 'My Market Share'}
            </span>
            <span className="kpi-value">
              {isPanel
                ? kpis.opportunities.market_penetration
                : kpis.opportunities.market_share}
              <span className="kpi-suffix"> %</span>
              <TrendArrow
                $dir={trend(
                  isPanel
                    ? kpis.opportunities.market_penetration
                    : kpis.opportunities.market_share,
                  isPanel
                    ? kpis.opportunities.market_penetration_prev
                    : kpis.opportunities.market_share_prev
                )}
              >
                {trendIcon(
                  trend(
                    isPanel
                      ? kpis.opportunities.market_penetration
                      : kpis.opportunities.market_share,
                    isPanel
                      ? kpis.opportunities.market_penetration_prev
                      : kpis.opportunities.market_share_prev
                  )
                )}
              </TrendArrow>
            </span>
          </KpiCard>
          <ClickableKpiCard
            onClick={() => navigateToRestaurants('to_win_over')}
          >
            <span className="kpi-label">To Win Over</span>
            <span className="kpi-value" style={{ color: '#e25d34' }}>
              {isPanel
                ? kpis.opportunities.panel_to_win_over
                : kpis.opportunities.to_win_over}
            </span>
          </ClickableKpiCard>
        </KpiRow>
      </Section>

      {/* By Market table */}
      <Section>
        <SectionTitle>By Market</SectionTitle>
        <Card>
          <TableWrapper>
            <table>
              <thead>
                <tr>
                  <th>Market</th>
                  <th className="num">DN</th>
                  <th className="num">Won</th>
                  <th className="num">Lost</th>
                  <th className="num">Win Back</th>
                  <th className="num">Cons. Rate</th>
                  <th className="num">Recurring</th>
                  <th className="num">
                    {isPanel ? 'Panel DN' : 'Competitor DN'}
                  </th>
                  <th className="num">
                    {isPanel ? 'Penetration' : 'Market Share'}
                  </th>
                  <th className="num">To Win Over</th>
                </tr>
              </thead>
              <tbody>
                {marketRows.map((row) => (
                  <tr
                    key={row.market}
                    className={
                      row.market === currentMarket ? 'active-market' : ''
                    }
                  >
                    <td>
                      <strong>{row.market}</strong>
                    </td>
                    <td className="num">{row.numerical_distribution}</td>
                    <td className="num" style={{ color: '#28a745' }}>
                      {row.won}
                    </td>
                    <td className="num" style={{ color: '#dc3545' }}>
                      {row.lost}
                    </td>
                    <td className="num" style={{ color: '#fd7e14' }}>
                      {row.win_back}
                    </td>
                    <td className="num">{row.consumption_rate}%</td>
                    <td className="num">{row.recurring_percent}%</td>
                    <td className="num">
                      {isPanel ? row.panel_dn : row.competitor_dn}
                    </td>
                    <td className="num">
                      {isPanel ? row.market_penetration : row.market_share}%
                    </td>
                    <td className="num">
                      {isPanel ? row.panel_to_win_over : row.to_win_over}
                    </td>
                  </tr>
                ))}
                {listStats && (
                  <tr className="list-stats">
                    <td>
                      <strong>{listStats.label}</strong>
                    </td>
                    <td className="num">{listStats.dn}</td>
                    <td className="num" style={{ color: '#28a745' }}>
                      {listStats.won}
                    </td>
                    <td className="num" style={{ color: '#dc3545' }}>
                      {listStats.lost}
                    </td>
                    <td className="num" style={{ color: '#fd7e14' }}>
                      {listStats.winBack}
                    </td>
                    <td className="num">{listStats.consumptionRate}%</td>
                    <td className="num">{listStats.recurring}%</td>
                    <td className="num">{listStats.competitorDn}</td>
                    <td className="num">{listStats.marketShare}%</td>
                    <td className="num">{listStats.toWinOver}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </TableWrapper>
        </Card>
      </Section>
    </OverviewContainer>
  );
};

export default Overview;
